import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import { GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { fetchDeletedFolders, restoreDeletedFolder, permanentlyDeleteFolder } from '../services/recycleBin.service';
import { PagedDeletedFoldersResult, RecycleBinQuery } from '../types/softDelete.types';
import RecycleBinFilters from './components/RecycleBinFilters';
import DeletedFoldersTable from './components/DeletedFoldersTable';
import RestoreConfirmModal from './components/RestoreConfirmModal';
import PermanentDeleteConfirmModal from './components/PermanentDeleteConfirmModal';
import { validatePageSelection } from './components/recycleBinTableConfig';

type BulkPrompt = { folderIds: string[]; selectedCount: number };

interface Props {
  orgId: string | undefined;
  page: number;
  limit: number;
  onPageChange: (page: number, limit: number) => void;
  onOrgIdChange: (orgId: string | undefined) => void;
}

export default function FoldersTab({ orgId, page, limit, onPageChange, onOrgIdChange }: Props) {
  const [foldersResult, setFoldersResult] = useState<PagedDeletedFoldersResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [restorePrompt, setRestorePrompt] = useState<BulkPrompt | null>(null);
  const [permanentPrompt, setPermanentPrompt] = useState<BulkPrompt | null>(null);
  const [restoreProcessing, setRestoreProcessing] = useState(false);
  const [permanentProcessing, setPermanentProcessing] = useState(false);

  const [localFilters, setLocalFilters] = useState<{ name?: string; clientName?: string }>({});

  // Same reasoning as RecordsTab: the Name/Client filters refetch on every keystroke with
  // no debounce, so an earlier-fired, broader query can resolve after a later, narrower
  // one and silently overwrite the result with a stale, unfiltered set.
  const fetchSeqRef = useRef(0);

  const fetchFolders = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (orgId) params.set('orgId', orgId);
      if (localFilters.name) params.set('name', localFilters.name);
      if (localFilters.clientName) params.set('clientName', localFilters.clientName);
      params.set('page', String(page));
      params.set('limit', String(limit));
      const data = (await fetchDeletedFolders(params.toString())) as PagedDeletedFoldersResult;
      if (seq !== fetchSeqRef.current) return;
      setFoldersResult(data);
    } catch (e: unknown) {
      if (seq !== fetchSeqRef.current) return;
      setError(e instanceof Error ? e.message : 'Failed to load deleted folders');
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [orgId, localFilters, page, limit]);

  useEffect(() => { void fetchFolders(); }, [fetchFolders]);

  const handleFiltersChange = (f: RecycleBinQuery) => {
    const { orgId: newOrg, name, clientName } = f;
    if (newOrg !== orgId) onOrgIdChange(newOrg);
    setLocalFilters({ name, clientName });
  };

  const openBulkRestore = () => {
    const ids = selectionModel.map(String);
    const rows = validatePageSelection(ids, foldersResult?.data ?? [], (r) => String(r._id));
    if (!rows) {
      setError('Some checked rows are not on this page. Changing page clears the selection — select again on one page.');
      setSelectionModel([]);
      return;
    }
    setRestorePrompt({ folderIds: rows.map((r) => String(r._id)), selectedCount: ids.length });
  };

  const openBulkPermanentDelete = () => {
    const ids = selectionModel.map(String);
    const rows = validatePageSelection(ids, foldersResult?.data ?? [], (r) => String(r._id));
    if (!rows) {
      setError('Some checked rows are not on this page. Changing page clears the selection — select again on one page.');
      setSelectionModel([]);
      return;
    }
    setPermanentPrompt({ folderIds: rows.map((r) => String(r._id)), selectedCount: ids.length });
  };

  const handleConfirmRestore = async () => {
    if (!restorePrompt) return;
    setRestoreProcessing(true);
    try {
      for (const fid of restorePrompt.folderIds) {
        await restoreDeletedFolder(fid);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Restore failed');
      setRestoreProcessing(false);
      return;
    }
    // See RecordsTab's handleConfirmRestore: the refresh below must never be able to
    // leave this dialog stuck on "Restoring…" if it's slow, fails, or never settles.
    setRestorePrompt(null);
    setSelectionModel([]);
    setRestoreProcessing(false);
    fetchFolders().catch(() => {
      setError('Restore succeeded, but the list could not be refreshed — reload the page to see the latest state.');
    });
  };

  const handleConfirmPermanentDelete = async () => {
    if (!permanentPrompt) return;
    setPermanentProcessing(true);
    try {
      for (const fid of permanentPrompt.folderIds) {
        await permanentlyDeleteFolder(fid);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Permanent delete failed');
      setPermanentProcessing(false);
      return;
    }
    setPermanentPrompt(null);
    setSelectionModel([]);
    setPermanentProcessing(false);
    fetchFolders().catch(() => {
      setError('Permanent delete succeeded, but the list could not be refreshed — reload the page to see the latest state.');
    });
  };

  const actionBusy = restoreProcessing || permanentProcessing;

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <RecycleBinFilters
        filters={{ ...localFilters, orgId, page, limit }}
        onChange={handleFiltersChange}
        onApply={() => { setSelectionModel([]); void fetchFolders(); }}
        showEntityDateFilters={false}
      />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {selectionModel.length > 0
            ? `${selectionModel.length} folder(s) selected`
            : 'Use the checkboxes to choose folders, then Restore or Permanently delete'}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            disabled={selectionModel.length === 0 || loading || actionBusy}
            onClick={openBulkRestore}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Restore selected{selectionModel.length > 0 ? ` (${selectionModel.length})` : ''}
          </Button>
          <Button
            variant="outlined"
            color="error"
            disabled={selectionModel.length === 0 || loading || actionBusy}
            onClick={openBulkPermanentDelete}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Permanently delete{selectionModel.length > 0 ? ` (${selectionModel.length})` : ''}
          </Button>
        </Box>
      </Box>
      <DeletedFoldersTable
        rows={foldersResult?.data ?? []}
        total={foldersResult?.total ?? 0}
        page={page}
        pageSize={limit}
        loading={loading}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={setSelectionModel}
        onPaginationModelChange={(model: GridPaginationModel) => {
          setSelectionModel([]);
          const limitChanged = model.pageSize !== limit;
          onPageChange(limitChanged ? 1 : model.page + 1, model.pageSize);
        }}
      />
      {restorePrompt && (
        <RestoreConfirmModal
          open
          variant="bulkFolders"
          selectedCount={restorePrompt.selectedCount}
          onConfirm={handleConfirmRestore}
          onClose={() => !restoreProcessing && setRestorePrompt(null)}
          isProcessing={restoreProcessing}
        />
      )}
      {permanentPrompt && (
        <PermanentDeleteConfirmModal
          open
          variant="bulkFolders"
          selectedCount={permanentPrompt.selectedCount}
          onConfirm={handleConfirmPermanentDelete}
          onClose={() => !permanentProcessing && setPermanentPrompt(null)}
          isProcessing={permanentProcessing}
        />
      )}
    </>
  );
}
